import { FC, FormEventHandler, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from 'components';
import { useResponse } from 'contexts/response';
import { formatCustomer } from 'utils/format';

import { AccountLinkField, savedMessage, useAccountLink } from './account-link';

/*
 * The account's fleet link as a page, reached from Customer details > Fleet File Account > EDIT
 * MAPPING. The state and the field live in `account-link.tsx`, shared with the dialog the Fleet Files
 * grid opens; this file only adds the page around them and returns to the account.
 */
const EditCustomerFleetFiles: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const customerId = useMemo(() => Number(params.id), [params.id]);
  const { setSuccess } = useResponse();
  const link = useAccountLink(customerId);

  const backToAccount = useCallback(() => {
    navigate(!customerId ? '/settings/customers' : `/settings/customers/${customerId}`, { replace: true });
  }, [navigate, customerId]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      if (await link.save()) {
        setSuccess(savedMessage(link.selectedCode));
        backToAccount();
      }
    },
    [backToAccount, link, setSuccess]
  );

  return (
    <Loader loading={!link.customer}>
      <form className="page" id="edit-customer" onReset={backToAccount} onSubmit={handleSubmit}>
        <div className="page-header">
          <h1>{formatCustomer(link.customer)}</h1>
          <div className="d-flex g-3 flex-row-reverse">
            <button className="btn-primary" type="submit" disabled={link.saving || !link.locationId}>
              {link.saving ? 'SAVING…' : 'SUBMIT'}
            </button>
            <button className="btn-outline" type="reset" disabled={link.saving}>
              CANCEL
            </button>
          </div>
        </div>
        <div className="page-content flex-fill">
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">Fleet File Account</h4>
            </div>
            <div className="card-body">
              <AccountLinkField link={link} />
            </div>
          </div>
        </div>
      </form>
    </Loader>
  );
};

export default EditCustomerFleetFiles;
